import type { IProspect, IProspectContact } from '../../interfaces/prospects';
import { ProspectContactType, ProspectSalesModel, type SalesModelSource } from '../../types/prospects';

export const EMPTY_FIELD = '—';

export const requiresHeadcountMinimum = (salesModel: ProspectSalesModel | null): boolean =>
  salesModel === ProspectSalesModel.AppAndInsurance;

export const getSalesModel = (prospect: SalesModelSource): ProspectSalesModel =>
  prospect?.salesModel ?? ProspectSalesModel.AppAndInsurance;

export const isAppOnly = (prospect: SalesModelSource): boolean =>
  getSalesModel(prospect) === ProspectSalesModel.AppOnly;

export const getPrimaryContact = (prospect: IProspect): IProspectContact | undefined =>
  prospect.contacts.find((contact) => contact.contactType === ProspectContactType.Administrative);

export const getBillingContact = (prospect: IProspect): IProspectContact | undefined =>
  prospect.contacts.find((contact) => contact.contactType === ProspectContactType.Billing);

export const isSameAsPrimaryContact = (contact: IProspectContact, prospect: IProspect): boolean => {
  const primary = getPrimaryContact(prospect);
  if (!primary) return false;
  return (
    contact.name === primary.name &&
    (contact.title ?? '') === (primary.title ?? '') &&
    (contact.email ?? '') === (primary.email ?? '') &&
    (contact.phone ?? '') === (primary.phone ?? '')
  );
};

export const hasDistinctBillingContact = (prospect: IProspect): boolean => {
  const billing = getBillingContact(prospect);
  return !!billing && !isSameAsPrimaryContact(billing, prospect);
};

export const formatProspectAddress = (prospect: IProspect): string => {
  const parts = [prospect.street, prospect.addressLine1, prospect.city, prospect.state, prospect.zipCode];
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => !!part)
    .join(', ');
};

export const upsertContact = (contacts: IProspectContact[], contact: IProspectContact): IProspectContact[] => {
  const index = contacts.findIndex((current) => current.contactType === contact.contactType);
  if (index === -1) return [...contacts, contact];
  const next = [...contacts];
  next[index] = contact;
  return next;
};
